import { RefObject, useEffect, useState } from 'react';

type UseClarificationTextareaMaxHeightParams = {
  activeQuestionSelector?: string;
  enabled: boolean;
  questionnaireContentRef?: RefObject<HTMLDivElement | null>;
  recalculationDependencies?: unknown[];
};

const TWO_LINE_TEXTAREA_HEIGHT = 60;
const SPACING_FROM_QUESTION = 24;

export const useClarificationTextareaMaxHeight = ({
  activeQuestionSelector = '[data-ai-active-question="true"]',
  enabled,
  questionnaireContentRef,
  recalculationDependencies = []
}: UseClarificationTextareaMaxHeightParams) => {
  const [maxTextareaHeight, setMaxTextareaHeight] = useState(
    TWO_LINE_TEXTAREA_HEIGHT
  );

  useEffect(() => {
    if (!enabled) return;

    const calculateMaxTextareaHeight = () => {
      const contentContainer = questionnaireContentRef?.current;
      const activeQuestion = document.querySelector<HTMLElement>(
        activeQuestionSelector
      );

      if (!contentContainer || !activeQuestion) {
        setMaxTextareaHeight(TWO_LINE_TEXTAREA_HEIGHT);
        return;
      }

      const contentContainerHeight = contentContainer.clientHeight;
      const activeQuestionHeight = activeQuestion.offsetHeight;
      const availableGrowthHeight =
        contentContainerHeight > activeQuestionHeight
          ? contentContainerHeight -
            activeQuestionHeight -
            SPACING_FROM_QUESTION
          : 0;

      setMaxTextareaHeight(
        Math.max(
          TWO_LINE_TEXTAREA_HEIGHT,
          TWO_LINE_TEXTAREA_HEIGHT + availableGrowthHeight
        )
      );
    };

    const animationFrameId = requestAnimationFrame(calculateMaxTextareaHeight);
    const activeQuestion = document.querySelector<HTMLElement>(
      activeQuestionSelector
    );
    const resizeObserver = new ResizeObserver(calculateMaxTextareaHeight);

    if (activeQuestion) resizeObserver.observe(activeQuestion);
    window.addEventListener('resize', calculateMaxTextareaHeight);
    window.visualViewport?.addEventListener(
      'resize',
      calculateMaxTextareaHeight
    );

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateMaxTextareaHeight);
      window.visualViewport?.removeEventListener(
        'resize',
        calculateMaxTextareaHeight
      );
    };
  }, [
    activeQuestionSelector,
    enabled,
    questionnaireContentRef,
    ...recalculationDependencies
  ]);

  return maxTextareaHeight;
};
